import * as React from "react"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import {
	Alert, Box, Button, Chip, CircularProgress, Dialog, DialogContent,
	Divider, IconButton, Link, Paper, Stack, Table, TableBody, TableCell,
	TableContainer, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material"
import SearchIcon from '@mui/icons-material/Search'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'

import { BACKEND_APIS, VISIBILITY_TRACKER_ROUTES } from "@constants"
import { useAppToast } from "@ui-seeker/app"
import PageWrapper from "Pages/@PageWrapper"
import { vtPageProps } from "../pageHeader"

import { vtApi as api } from "../api"


// `origin_user` / `captured_by` come from the API as `{id_user, email}` (the
// Resources page already renders them that way); this page interpolated the
// object itself and printed "[object Object]". Same label as Resources:
// the local part of the email, or the id when the email is missing.
const userLabel = (u) => {
	if (!u) return '';
	if (typeof u === 'string') return u.split('@')[0];
	return u.email ? u.email.split('@')[0] : `user #${u.id_user}`;
};

function formatTs(ts) {
	if (!ts) return '—'
	return String(ts).replace('T', ' ').slice(0, 16)
}

// tagging_json keys already surfaced as dedicated blocks (brands,
// categories, url) or that are internal plumbing — everything else is
// rendered generically so new tagging fields show up without a front
// change.
const _TAGGING_SKIP = new Set(['brands', 'categories', 'destination_url', 'unmatched_brands'])

function TaggingBlock({ tagging }) {
	const brands = Array.isArray(tagging.brands) ? tagging.brands : []
	const categories = Array.isArray(tagging.categories) ? tagging.categories : []
	const unmatched = Array.isArray(tagging.unmatched_brands) ? tagging.unmatched_brands : []
	const rest = Object.entries(tagging)
		.filter(([k, v]) => !_TAGGING_SKIP.has(k) && v !== null && v !== '' && v !== undefined)
	return <Stack spacing={1}>
		{brands.length > 0 && (
			<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
				<Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 90 }}>Brands</Typography>
				{brands.map((b, i) => (
					<Chip key={i} size="small" color="primary" variant="outlined"
						label={typeof b === 'object' ? (b.name || b.id_brand) : String(b)} />
				))}
			</Stack>
		)}
		{unmatched.length > 0 && (
			<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
				<Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 90 }}>Unmatched</Typography>
				{unmatched.map((b, i) => (
					<Chip key={i} size="small" color="warning" variant="outlined" label={String(b)} />
				))}
			</Stack>
		)}
		{categories.length > 0 && (
			<Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
				<Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 90 }}>Categories</Typography>
				{categories.map((c, i) => (
					<Chip key={i} size="small" color="secondary" variant="outlined"
						label={typeof c === 'object' ? (c.name || c.id_category) : String(c)} />
				))}
			</Stack>
		)}
		{tagging.destination_url && (
			<Stack direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
				<Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 90 }}>URL</Typography>
				<Link href={tagging.destination_url} target="_blank" rel="noreferrer"
					variant="caption" sx={{ wordBreak: 'break-all' }}
				>
					{tagging.destination_url} <OpenInNewIcon sx={{ fontSize: 11, verticalAlign: 'middle' }} />
				</Link>
			</Stack>
		)}
		{rest.map(([k, v]) => (
			<Stack key={k} direction="row" spacing={0.5} sx={{ alignItems: 'center' }}>
				<Typography variant="caption" sx={{ color: 'text.disabled', minWidth: 90 }}>{k}</Typography>
				<Typography variant="caption" sx={{ fontFamily: typeof v === 'object' ? 'monospace' : 'inherit', wordBreak: 'break-all' }}>
					{typeof v === 'object' ? JSON.stringify(v) : String(v)}
				</Typography>
			</Stack>
		))}
	</Stack>
}

export default function ResourceDetailPage() {
	const { showToast } = useAppToast()
	const [searchParams, setSearchParams] = useSearchParams()
	const urlId = searchParams.get('id') || ''
	const [inputId, setInputId] = useState(urlId)
	const [resource, setResource] = useState(null)
	const [loading, setLoading] = useState(false)
	const [notFound, setNotFound] = useState(null)
	const [lightboxSrc, setLightboxSrc] = useState(null)

	const load = async (id) => {
		if (!id) return
		setLoading(true)
		setNotFound(null)
		setResource(null)
		const r = await api.get(`${BACKEND_APIS.VT}/admin/resources/${id}`)
		if (r.ok) setResource(r.data)
		else if (r.status === 404) setNotFound(id)
		else showToast(`Failed to load resource #${id}: ${r.error}`, 'error')
		setLoading(false)
	}

	// The URL is the source of truth: ?id= loads on mount AND whenever it
	// changes (deep links from other pages keep working while the page is
	// already open).
	useEffect(() => {
		setInputId(urlId)
		if (urlId) load(urlId)
	}, [urlId])

	const go = () => {
		const clean = String(inputId).trim().replace(/^#/, '')
		if (!clean || !/^\d+$/.test(clean)) {
			showToast('Enter a numeric resource id', 'warning')
			return
		}
		if (clean === urlId) load(clean)
		else setSearchParams({ id: clean })
	}

	const r = resource
	const occurrences = r?.occurrences || []
	return <PageWrapper {...vtPageProps('Resource Lookup', 'Inspect one VT resource: full data + occurrence ledger')}>
		<Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
			<Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
				<TextField
					size="small" label="Resource id" value={inputId}
					onChange={e => setInputId(e.target.value)}
					onKeyDown={e => { if (e.key === 'Enter') go() }}
					sx={{ width: 180 }}
				/>
				<Button variant="contained" size="small" startIcon={<SearchIcon />} onClick={go}>
					Open
				</Button>
				<Box sx={{ flex: 1 }} />
				{r && (
					<Button
						size="small" variant="outlined"
						component="a"
						href={`${VISIBILITY_TRACKER_ROUTES.RESOURCES_PATH}?retailer=${r.id_retailer}`}
					>
						Open Resources dashboard
					</Button>
				)}
			</Stack>
		</Paper>

		{loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>}
		{notFound && <Alert severity="warning">Resource #{notFound} not found.</Alert>}
		{!loading && !r && !notFound && (
			<Alert severity="info">
				Enter a resource id (or open this page as <code>?id=123</code>) to inspect it.
			</Alert>
		)}

		{r && !loading && <>
			{/* ── Master card ─────────────────────────────────────── */}
			<Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
					{r.crop_inlined && (
						<Box component="img" src={r.crop_inlined} alt=""
							onClick={() => setLightboxSrc(r.crop_inlined)}
							sx={{ maxWidth: 280, maxHeight: 280, objectFit: 'contain', cursor: 'zoom-in',
								border: '1px solid', borderColor: 'divider', borderRadius: 1 }}
						/>
					)}
					<Stack spacing={0.8} sx={{ flex: 1, minWidth: 0 }}>
						<Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
							<Typography variant="h6" sx={{ mr: 1 }}>
								#{r.id_resource} · {r.retailer?.name || r.retailer?.slug}
							</Typography>
							{r.retailer?.country && <Chip size="small" label={r.retailer.country} variant="outlined" />}
							{r.kind && <Chip size="small" color="primary" variant="outlined"
								label={r.parent_kind && r.parent_kind !== r.kind ? `${r.kind} (${r.parent_kind})` : r.kind} />}
							{r.ignored && <Chip size="small" label="ignored" variant="outlined" />}
							{r.needs_qa
								? <Chip size="small" color="error" label="needs QA" />
								: <Chip size="small" color="success" variant="outlined" label="validated" />}
							{r.intent && <Chip size="small" variant="outlined" label={`intent: ${r.intent}`} />}
							{r.composition && <Chip size="small" variant="outlined" label={`composition: ${r.composition}`} />}
						</Stack>
						{r.merged_into && (
							// A tombstone: its sightings now live on the target, so
							// "Occurrences (0)" below is correct and `count_seen` is
							// the frozen pre-merge figure. Say so, and offer the way
							// out — before this line the page looked like data loss.
							<Alert severity="info" sx={{ my: 1 }}
								action={<Button size="small" onClick={() => setSearchParams({ id: String(r.merged_into) })}>Open #{r.merged_into}</Button>}>
								Merged into <strong>#{r.merged_into}</strong> — this row is a tombstone. Its sightings were re-linked to the target; the counters here are frozen at merge time.
							</Alert>
						)}
						<Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
							pHash {r.phash}
						</Typography>
						<Typography variant="caption" sx={{ color: 'text.secondary' }}>
							Seen <strong>{r.count_seen}</strong> time(s) · first <em>{formatTs(r.first_seen_at)}</em> · last <em>{formatTs(r.last_seen_at)}</em>{r.ts_created ? <> · created <em>{formatTs(r.ts_created)}</em></> : null}
						</Typography>
						<Typography variant="caption" sx={{ color: 'text.secondary' }}>
							Origin task #{r.id_task_origin}{r.origin_user ? ` · captured by ${userLabel(r.origin_user)}` : ''}
							{r.reviewed_by ? ` · reviewed by ${r.reviewed_by} (${formatTs(r.reviewed_at)})` : ''}
						</Typography>
						{(r.sections_seen || []).length > 0 && (
							<Stack direction="row" spacing={0.4} sx={{ flexWrap: 'wrap', gap: 0.3 }}>
								{r.sections_seen.map(s => (
									<Tooltip key={s.id_section} title={`Last seen ${formatTs(s.last_seen_in_section)}`}>
										<Chip size="small" variant="outlined" label={s.slug || s.name} />
									</Tooltip>
								))}
							</Stack>
						)}
						{r.qa_reason && (
							<Alert severity="warning" variant="outlined" sx={{ py: 0.4 }}>
								<Typography variant="caption">{r.qa_reason}</Typography>
							</Alert>
						)}
						{r.crop_path && (
							<Typography variant="caption" sx={{ color: 'text.disabled', fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 10 }}>
								{r.crop_path}
							</Typography>
						)}
					</Stack>
				</Stack>
				<Divider sx={{ my: 2 }} />
				<Typography variant="subtitle2" sx={{ mb: 1 }}>Tagging</Typography>
				<TaggingBlock tagging={r.tagging_json || {}} />
			</Paper>

			{/* ── Occurrence ledger ────────────────────────────────── */}
			<Paper variant="outlined" sx={{ p: 2 }}>
				<Typography variant="subtitle2" sx={{ mb: 1 }}>
					Occurrences ({occurrences.length})
				</Typography>
				<TableContainer sx={{ overflowX: 'auto' }}>
					<Table size="small">
						<TableHead>
							<TableRow>
								<TableCell>Crop</TableCell>
								<TableCell>Occurrence</TableCell>
								<TableCell>Task</TableCell>
								<TableCell>Section</TableCell>
								<TableCell>Distance</TableCell>
								<TableCell>Seen at</TableCell>
								<TableCell>Captured by</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{occurrences.map(o => (
								<TableRow key={o.id_occurrence} hover>
									<TableCell>
										{o.crop_inlined
											? <Box component="img" src={o.crop_inlined} alt=""
												onClick={() => setLightboxSrc(o.crop_inlined)}
												sx={{ maxWidth: 90, maxHeight: 56, objectFit: 'contain', cursor: 'zoom-in' }} />
											: <Typography variant="caption" sx={{ color: 'text.disabled' }}>—</Typography>}
									</TableCell>
									<TableCell>#{o.id_occurrence}</TableCell>
									<TableCell>#{o.id_task}</TableCell>
									<TableCell>{o.section ? (o.section.slug || o.section.name) : '—'}</TableCell>
									<TableCell>
										{o.hamming_distance === 0
											? <Chip size="small" color="success" variant="outlined" label="exact" />
											: <Chip size="small" variant="outlined" label={`dist ${o.hamming_distance}`} />}
									</TableCell>
									<TableCell>{formatTs(o.seen_at)}</TableCell>
									<TableCell>{o.captured_by ? userLabel(o.captured_by) : '—'}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			</Paper>
		</>}

		{/* Simple lightbox for master / occurrence crops */}
		<Dialog open={Boolean(lightboxSrc)} onClose={() => setLightboxSrc(null)} maxWidth={false}
			PaperProps={{ sx: { bgcolor: 'rgba(0,0,0,0.85)', m: 0, maxWidth: '95vw', maxHeight: '95vh' } }}
		>
			<DialogContent sx={{ p: 1, position: 'relative' }} onClick={() => setLightboxSrc(null)}>
				<IconButton onClick={() => setLightboxSrc(null)}
					sx={{ position: 'absolute', top: 8, right: 8, color: 'common.white', zIndex: 1 }}
				>
					<CloseIcon />
				</IconButton>
				{lightboxSrc && (
					<Box component="img" src={lightboxSrc} alt=""
						sx={{ maxWidth: '90vw', maxHeight: '90vh', objectFit: 'contain', display: 'block' }} />
				)}
			</DialogContent>
		</Dialog>
	</PageWrapper>
}
